import { Check, Lightbulb, MessageCircle, FileText, Eye, Hammer, PartyPopper } from 'lucide-react';
import type { GuidedPhase, BuildPlan } from '../../types/guided-builder';

const PHASES: Array<{ key: GuidedPhase; label: string; icon: React.ElementType }> = [
  { key: 'ideation', label: 'Idea', icon: Lightbulb },
  { key: 'clarification', label: 'Clarify', icon: MessageCircle },
  { key: 'planning', label: 'Plan', icon: FileText },
  { key: 'assumptions', label: 'Review', icon: Eye },
  { key: 'execution', label: 'Build', icon: Hammer },
  { key: 'complete', label: 'Done', icon: PartyPopper },
];

const PHASE_ORDER: Record<GuidedPhase, number> = {
  ideation: 0,
  clarification: 1,
  planning: 2,
  assumptions: 3,
  execution: 4,
  complete: 5,
};

interface GuidedPhaseBarProps {
  currentPhase: GuidedPhase;
  buildPlan?: BuildPlan | null;
}

export function GuidedPhaseBar({ currentPhase, buildPlan }: GuidedPhaseBarProps) {
  const currentIndex = PHASE_ORDER[currentPhase];

  // Sub-progress for execution phase
  const stepProgress = buildPlan?.approved
    ? `Step ${(buildPlan.currentStepIndex ?? 0) + 1} of ${buildPlan.steps.length}`
    : null;

  return (
    <div className="flex items-center gap-1 px-4 py-2 border-b bg-muted/30 min-h-[40px]">
      {PHASES.map((phase, index) => {
        const isCompleted = index < currentIndex;
        const isActive = index === currentIndex;
        const Icon = phase.icon;

        return (
          <div key={phase.key} className="flex items-center gap-1">
            {index > 0 && (
              <div className={`h-px w-3 sm:w-6 ${isCompleted ? 'bg-primary' : 'bg-border'}`} />
            )}
            <div
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${
                isActive
                  ? 'bg-primary text-primary-foreground font-medium'
                  : isCompleted
                    ? 'text-primary'
                    : 'text-muted-foreground'
              }`}
            >
              {isCompleted ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{phase.label}</span>
            </div>
          </div>
        );
      })}

      {/* Step sub-progress during execution */}
      {currentPhase === 'execution' && stepProgress && (
        <span className="ml-auto text-xs text-muted-foreground">{stepProgress}</span>
      )}
    </div>
  );
}
